import MixcloudCloudcast from '../mixcloud/mixcloudCloudcast';

/**
 * Guessing the DJ from a Mixcloud show's title.
 *
 * **This is the only place in Fadila that guesses at metadata**, and it is
 * deliberately quarantined here so it can be deleted in one go: remove this
 * file, the two lines that call it in mixcloudTrack.ts, and the setting.
 * Nothing else depends on it.
 *
 * Why it exists: Mixcloud has no artist field of any kind (docs/MIXCLOUD.md
 * section 4c). A set by Dax J uploaded by an archive account is credited to
 * the archive, and the DJ's name only exists inside the title. So the artist
 * column read "Techno Archive" for eight different Dax J sets.
 *
 * Why it is only two rules: measured against live search results, anything
 * looser is wrong more often than right. Matching *any* tag against the title
 * produced artists called "Techno", "Exit Festival", "Bongoflava" and
 * "EarToGround"; taking the last dash-separated segment turned "Dax J -
 * Spring Mix 2012 - Techno" into "Techno". Both were dropped.
 *
 * What it deliberately does not do is guess from a bare "X - Y" title. Half
 * of Mixcloud is titled that way and the leading half is as often the show,
 * the series or the venue as the DJ.
 */

/** How the artist was arrived at - for the doc, and for debugging. */
export type ArtistSource = 'at' | 'dash-confirmed-by-tag';

export interface GuessedArtist {
  artist: string;
  via: ArtistSource;
}

/** "2019-07-19 - Dax J @ Sónar" -> "Dax J @ Sónar". */
const leadingDate = /^\s*\d{4}[-.]\d{2}[-.]\d{2}\s*[-–]\s*/;

/** A DJ name longer than this is a sentence, and the guess is wrong. */
const maxArtistLength = 60;

/**
 * The DJ, if the title says so plainly enough to be worth believing, and
 * undefined when it does not - in which case the caller keeps the uploader.
 */
export default function mixcloudArtistFromTitle(
  cloudcast: MixcloudCloudcast,
): GuessedArtist | undefined {
  const title = (cloudcast.name || '').replace(leadingDate, '').trim();

  if (!title) {
    return undefined;
  }

  /*
    Rule 1: "<artist> @ <venue>".

    The @ is how a live set is titled - "Dax J @ Awakenings ADE Closing
    party", "2019-07-07 - Dax J @ Exit Festival, Novi Sad" - and what stands
    in front of it is the person playing. The strongest of the two by some
    way: every match across two sampled searches was right.
  */
  const atVenue = title.split(/\s+@\s+/);

  if (atVenue.length > 1) {
    const candidate = atVenue[0]
      // "Dax J - live @ Mayday Poland 2017"
      .replace(/\s+[-–]\s*live$/i, '')
      .replace(/\s+live$/i, '')
      .trim();

    if (candidate && candidate.length <= maxArtistLength) {
      return { artist: candidate, via: 'at' };
    }
  }

  /*
    Rule 2: a leading "<artist> - ..." that the uploader *also tagged*.

    The tag is what turns a guess into a confirmation: "2020-04-03 - Dax J -
    Isolation Mix" is tagged "Dax J", so the leading segment is not a series
    name or a venue. Without the tag this rule would fire on every second
    show and be wrong on most of them.
  */
  const segments = title.split(/\s+[-–|]\s+/);

  if (segments.length > 1) {
    const first = segments[0].trim();
    const tagged = (cloudcast.tags || []).some(
      (tag) => tag.name.toLowerCase() === first.toLowerCase(),
    );

    if (first && tagged && first.length <= maxArtistLength) {
      return { artist: first, via: 'dash-confirmed-by-tag' };
    }
  }

  return undefined;
}
